import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heading } from '../../components/heading';
import { Text } from '../../components/text';
import { Input } from '../../components/input';
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '../../components/table';
import TablePager from '../../components/TablePager';
import { ChevronRightIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { aggregateProveedores, dueLabel, formatFechaCorta } from '../../utils/convenioCompliance';
import { ConvenioPicker, EmptyScope, StatTile, TODOS, fmtMXN, useConvenioScope } from './convenioScope';

const perPage = 10;
const MS_PER_DAY = 86400000;

/**
 * Proveedores en riesgo — the ranking of fiados that owe the convenio a fianza.
 *
 * aggregateProveedores keys on the normalized proveedor name, so the same
 * supplier working under two convenios lands on ONE row: the whole relationship
 * in one place, which is the point of the cross-convenio mode.
 *
 * No year filter: an overdue requisito does not stop being overdue in January.
 */
export function ProveedoresRiesgo() {
  const navigate = useNavigate();
  const [convenioId, setConvenioId] = useState(TODOS);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState(() => new Set());
  const {
    options,
    scopedContracts,
    beneficiariosById,
    proyectosById,
    convenioDeContrato,
    loading,
  } = useConvenioScope(convenioId);

  useEffect(() => {
    setPage(1);
    setExpanded(new Set());
  }, [convenioId, searchTerm]);

  const rows = useMemo(() => {
    const now = Date.now();
    const agg = aggregateProveedores(scopedContracts, beneficiariosById, proyectosById);
    return agg
      .map((p) => {
        const convenios = new Map();
        p.contracts.forEach((cid) => {
          const g = convenioDeContrato.get(cid);
          if (g) convenios.set(g.id, g.nombre);
        });
        // Pendientes worth chasing: everything except the futuras, which cannot
        // be issued yet by definition.
        const accionables = p.pendientes.filter((fr) => !fr.isFutura);
        const diasAtraso =
          p.oldestOverdueDate != null ? Math.floor((now - p.oldestOverdueDate) / MS_PER_DAY) : null;
        return {
          ...p,
          convenios: Array.from(convenios.entries()).map(([id, nombre]) => ({ id, nombre })),
          accionables,
          diasAtraso,
          expuesto: p.montos.overdue + p.montos.porEmitir,
        };
      })
      .filter((p) => p.counts.overdue > 0 || p.counts.porEmitir > 0 || p.counts.vencida > 0);
  }, [scopedContracts, beneficiariosById, proyectosById, convenioDeContrato]);

  const filtered = useMemo(() => {
    if (!searchTerm) return rows;
    const q = searchTerm.toLowerCase();
    return rows.filter(
      (r) =>
        (r.label || '').toLowerCase().includes(q) ||
        r.convenios.some((c) => c.nombre.toLowerCase().includes(q))
    );
  }, [rows, searchTerm]);

  const totales = useMemo(
    () =>
      rows.reduce(
        (acc, r) => ({
          proveedores: acc.proveedores + 1,
          conAtraso: acc.conAtraso + (r.counts.overdue > 0 ? 1 : 0),
          atrasadas: acc.atrasadas + r.counts.overdue,
          montoAtrasado: acc.montoAtrasado + r.montos.overdue,
          porEmitir: acc.porEmitir + r.counts.porEmitir,
          expuesto: acc.expuesto + r.expuesto,
        }),
        { proveedores: 0, conAtraso: 0, atrasadas: 0, montoAtrasado: 0, porEmitir: 0, expuesto: 0 }
      ),
    [rows]
  );

  const toggle = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const paged = filtered.slice((page - 1) * perPage, page * perPage);

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <Heading level={2} className="text-xl text-azul-surelia">Proveedores en riesgo</Heading>
          <Text className="text-sm text-azul-surelia/70">
            Proveedores con fianzas atrasadas, por emitir o vencidas sin cancelar
          </Text>
        </div>
        <ConvenioPicker value={convenioId} onChange={setConvenioId} options={options} />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[240px]">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-azul-surelia"></div>
        </div>
      ) : rows.length === 0 ? (
        <EmptyScope>Ningún proveedor tiene fianzas atrasadas, por emitir ni vencidas.</EmptyScope>
      ) : (
        <>
          {/* El buscador va en su propio renglón: como hermano flex de los
              tiles les robaba ancho y el importe expuesto ya no cabía. */}
          <div className="mb-4 space-y-3">
            <Input
              type="text"
              placeholder="Buscar proveedor o convenio..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:max-w-sm"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatTile
                label="Proveedores"
                value={totales.proveedores}
                sub={`${totales.conAtraso} con atraso`}
                alert={totales.conAtraso > 0}
              />
              <StatTile
                label="Atrasadas"
                value={totales.atrasadas}
                sub={fmtMXN(totales.montoAtrasado)}
                alert={totales.atrasadas > 0}
              />
              <StatTile label="Por emitir" value={totales.porEmitir} />
              <StatTile label="Monto expuesto" value={fmtMXN(totales.expuesto)} sub="sin garantía viva" />
            </div>
          </div>

          {filtered.length === 0 ? (
            <EmptyScope>Sin resultados.</EmptyScope>
          ) : (
            <>
              <div className="overflow-x-auto border border-azul-surelia/15 rounded-lg">
                <Table>
                  <TableHead className="bg-azul-surelia text-white">
                    <TableRow>
                      <TableHeader className="text-white w-8"></TableHeader>
                      <TableHeader className="text-white">Proveedor</TableHeader>
                      <TableHeader className="text-white">Convenio</TableHeader>
                      <TableHeader className="text-white text-right">Contratos</TableHeader>
                      <TableHeader className="text-white text-right">Atrasadas</TableHeader>
                      <TableHeader className="text-white text-right">Atraso</TableHeader>
                      <TableHeader className="text-white text-right">Por emitir</TableHeader>
                      <TableHeader className="text-white text-right">Vencidas</TableHeader>
                      <TableHeader className="text-white text-right">Expuesto</TableHeader>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paged.map((r) => {
                      const isOpen = expanded.has(r.key);
                      return (
                        <React.Fragment key={r.key}>
                          <TableRow className="cursor-pointer hover:bg-azul-surelia/5" onClick={() => toggle(r.key)}>
                            <TableCell className="py-3 pr-0">
                              {isOpen ? (
                                <ChevronDownIcon className="w-4 h-4 text-zinc-400" />
                              ) : (
                                <ChevronRightIcon className="w-4 h-4 text-zinc-400" />
                              )}
                            </TableCell>
                            <TableCell className="py-3">
                              <Text className="text-sm font-medium text-azul-surelia truncate max-w-[260px]" title={r.label}>
                                {r.label}
                              </Text>
                              {r.rfc && <Text className="text-[11px] text-zinc-400 font-mono">{r.rfc}</Text>}
                            </TableCell>
                            <TableCell className="py-3">
                              <Text
                                className="text-sm text-zinc-700 truncate max-w-[200px]"
                                title={r.convenios.map((c) => c.nombre).join(', ')}
                              >
                                {r.convenios.length === 1
                                  ? r.convenios[0].nombre
                                  : `${r.convenios.length} convenios`}
                              </Text>
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {r.contracts.length}
                            </TableCell>
                            <TableCell className="py-3 text-right">
                              <Text
                                className={`text-sm tabular-nums ${
                                  r.counts.overdue > 0 ? 'font-semibold text-red-600' : 'text-zinc-400'
                                }`}
                              >
                                {r.counts.overdue || '-'}
                              </Text>
                            </TableCell>
                            <TableCell className="py-3 text-right">
                              {r.diasAtraso == null ? (
                                <span className="text-sm text-zinc-400">-</span>
                              ) : (
                                <Text className="text-sm text-red-600 tabular-nums" title={formatFechaCorta(r.oldestOverdueDate)}>
                                  {r.diasAtraso} d
                                </Text>
                              )}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {r.counts.porEmitir || '-'}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm text-zinc-700">
                              {r.counts.vencida || '-'}
                            </TableCell>
                            <TableCell className="py-3 text-right tabular-nums text-sm font-semibold text-azul-surelia">
                              {fmtMXN(r.expuesto)}
                            </TableCell>
                          </TableRow>
                          {isOpen && (
                            <TableRow className="bg-azul-surelia/5">
                              <TableCell colSpan={9} className="py-2 px-6">
                                <div className="sticky left-0 space-y-1">
                                  {r.accionables.length === 0 && (
                                    <Text className="text-sm text-zinc-500">
                                      Sin requisitos exigibles; sólo fianzas vencidas sin cancelar.
                                    </Text>
                                  )}
                                  {r.accionables.map((fr) => (
                                    <button
                                      key={fr._key}
                                      onClick={() => {
                                        const grupo = convenioDeContrato.get(fr.contractId);
                                        if (grupo) navigate(`/app/convenios/${grupo.id}?tab=proveedores`);
                                      }}
                                      className="w-full flex flex-wrap items-center gap-x-4 gap-y-1 text-left bg-white rounded-lg border border-zinc-200 px-3 py-1.5 hover:border-azul-surelia/40 transition-colors"
                                    >
                                      <span className="text-sm text-zinc-800 min-w-[120px]">{fr.tipo}</span>
                                      <span className="text-xs text-zinc-500 min-w-[200px] truncate" title={fr.contractTitle}>
                                        {fr.contractNumero || fr.contractTitle}
                                      </span>
                                      {fr.proyectoNombre && (
                                        <span className="text-xs text-zinc-500 truncate">{fr.proyectoNombre}</span>
                                      )}
                                      <span
                                        className={`px-2 py-0.5 text-xs font-semibold rounded-full ${
                                          fr.isOverdue ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                                        }`}
                                      >
                                        {dueLabel(fr)}
                                      </span>
                                      <span className="text-xs text-zinc-600 tabular-nums ml-auto">
                                        {fmtMXN(fr.monto)}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <TablePager
                total={filtered.length}
                page={page}
                onPage={setPage}
                perPage={perPage}
                itemLabel="proveedores"
              />
            </>
          )}
        </>
      )}
    </div>
  );
}
